import React, { useState } from "react";
import ConfirmationModal from "./ConfirmationModal";
import { useSingleWallet } from "../../../hooks/useWallet";
import { WalletCurrency } from "../../../types/wallet.types";
import { extractAxoisErrorsMessage } from "../../../../errorHandler/utils/errors.utils";
import AddManualCreditFormView from "./AddManualCreditFormView";

export interface DataFormAddManualCredit {
  amount?: number;
  currency?: WalletCurrency;
}

export interface ErrorStatus {
  allgood: boolean;
  message?: string;
  isErrorAmount?: boolean;
  isErrorCurrency?: boolean;
}

const INITIAL_ERROR_STATUS: ErrorStatus = {
  allgood: true,
  message: "",
  isErrorAmount: false,
  isErrorCurrency: false,
};

export default function AddManualCreditForm({
  onSuccess,
  walletId,
}: {
  onSuccess?: () => void;
  walletId?: string;
}) {
  const [dataForm, setDataForm] = useState<DataFormAddManualCredit>({
    currency: "FCFA", // Devise par défaut
  });

  const [errorStatus, setErrorStatus] = useState<ErrorStatus>({
    ...INITIAL_ERROR_STATUS,
  });

  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const { addManualCreditQuery } = useSingleWallet(walletId);

  const isFormValid = (): ErrorStatus => {
    let message = [];
    let errorStatusNew = { ...INITIAL_ERROR_STATUS };

    if (!dataForm.amount || dataForm.amount <= 0) {
      errorStatusNew.allgood = false;
      errorStatusNew.isErrorAmount = true;
      message.push("Veuillez saisir un montant valide supérieur à 0");
    }

    if (!dataForm.currency) {
      errorStatusNew.allgood = false;
      errorStatusNew.isErrorCurrency = true;
      message.push("Veuillez sélectionner une devise");
    }

    if (message.length > 0) {
      errorStatusNew.message = message.join(" ");
    } else {
      errorStatusNew.allgood = true;
      errorStatusNew.message = "";
    }

    return errorStatusNew;
  };

  const handleClickSubmit = () => {
    const validationResult = isFormValid();
    
    if (!validationResult.allgood) {
      setErrorStatus(validationResult);
      return;
    }

    // Ouvrir la modale de confirmation
    setShowConfirmationModal(true);
  };

  const handleConfirmAddCredit = () => {
    addManualCreditQuery.mutate(
      {
        amount: dataForm.amount || 0,
        unit: dataForm.currency || "FCFA",
      },
      {
        onSuccess: () => {
          setShowConfirmationModal(false);
          onSuccess && onSuccess();
        },
        onError: (error: any) => {
          setShowConfirmationModal(false);
          setErrorStatus({
            ...errorStatus,
            allgood: false,
            message: extractAxoisErrorsMessage(error) || "Erreur lors de l'ajout du crédit manuel",
          });
        },
      }
    );
  };

  const handleCancelConfirmation = () => {
    setShowConfirmationModal(false);
  };

  const setDataFormValue = (key: keyof DataFormAddManualCredit, value: any) => {
    setDataForm({
      ...dataForm,
      [key]: value,
    });
    
    // Reset error status when user modifies form
    if (errorStatus.message) {
      setErrorStatus(INITIAL_ERROR_STATUS);
    }
  };

  return (
    <>
      <AddManualCreditFormView
        setDataForm={setDataFormValue}
        dataForm={dataForm}
        handleSubmit={handleClickSubmit}
        isLoading={addManualCreditQuery.isLoading}
        errorStatus={errorStatus}
        disableSubmit={!isFormValid().allgood || addManualCreditQuery.isLoading}
        submitLabel="Ajouter le crédit manuel"
      />
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onConfirm={handleConfirmAddCredit}
        onCancel={handleCancelConfirmation}
        amount={dataForm.amount || 0}
        currency={dataForm.currency || "FCFA"}
        isLoading={addManualCreditQuery.isLoading}
      />
    </>
  );
}
